import { FC } from 'react';
import { ComponentFields } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import toLower from 'lodash-es/toLower';

import { ComponentProps } from '../../../types';
import { isColorBright, resolveColorProperty } from '../../colorUtils';
import {
  JabraTheme,
  JabraThemeProvider,
  ThemeStyle,
  useJabraTheme,
  useJabraThemesList,
} from '../../contexts';

export interface ThemedOptions {
  defaultTheme?: JabraTheme;
  legacyBackgroundFieldName?: string;
}

export const withTheme = <
  F extends Record<string, unknown> = Record<string, unknown>,
  S = object,
  P extends ComponentProps<F, S> = ComponentProps<F, S>,
>({ legacyBackgroundFieldName: backgroundFieldName, defaultTheme }: ThemedOptions = {}) => {
  return (WrappedComponent: FC<ComponentProps<F, S>>): FC<ComponentProps<F, S>> => {
    const ThemedComponent = (props: P) => {
      const parentTheme = useJabraTheme();
      const themes = useJabraThemesList();
      let finalTheme = defaultTheme;

      const legacyBg =
        !!backgroundFieldName &&
        ((props.fields?.[backgroundFieldName] as { value?: unknown })?.value ??
          (!!props.rendering?.fields &&
            (
              (props.rendering.fields as ComponentFields)[backgroundFieldName] as {
                value?: unknown;
              }
            )?.value));

      const legacyBgHex = !!legacyBg && resolveColorProperty(legacyBg as string);
      if (legacyBgHex) {
        const isDark = !isColorBright(legacyBgHex);
        finalTheme = {
          backgroundColor: legacyBgHex,
          darkTheme: isDark,
          foregroundColor: isDark ? 'white' : 'black',
        };
      } else {
        if (parentTheme) {
          finalTheme = parentTheme;
        } else {
          const currentThemeName = props.params?.['Theme'];
          const jabraTheme = themes.find(
            (themeItem) => toLower(themeItem?.name) === toLower(currentThemeName)
          );

          if (jabraTheme?.backgroundColor) {
            finalTheme = {
              darkTheme: jabraTheme.darkTheme?.value ?? false,
              backgroundColor: jabraTheme.backgroundColor?.value,
              foregroundColor: jabraTheme.foregroundColor?.value,
            };
          }
        }
      }
      const styles: ThemeStyle = {};
      const { backgroundColor, foregroundColor, darkTheme = false } = finalTheme ?? {};
      if (backgroundColor) {
        styles['--bgColor'] = backgroundColor;
      }
      if (foregroundColor) {
        styles['--fgColor'] = foregroundColor;
      }
      return (
        <div
          className={classNames('themed-container', darkTheme ? 'dark' : 'light')}
          style={styles}
        >
          <JabraThemeProvider theme={finalTheme}>
            <WrappedComponent {...props} />
          </JabraThemeProvider>
        </div>
      );
    };
    ThemedComponent.displayName = 'ThemedComponent';
    return ThemedComponent;
  };
};
